import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
} from "react";
import {
  CheckCircle2,
  XCircle,
  CircleDot,
  ArrowLeft,
  ArrowRight,
  Award,
  BarChart2,
  GraduationCap,
  FileQuestion,
  Loader2,
} from "lucide-react";
import { AppContext } from "../AppContext";
import { useLearner } from "../LearnerContext";
import { recordActivity } from "../learnerApi";

const QuizPage = () => {
  const { fetchQuestions } = useContext(AppContext);
  // Trắc nghiệm dành cho học viên trung tâm: chưa mở khoá thì hiện popup nhập SĐT phụ huynh rồi tải lại
  const { openGate } = useLearner();
  const [selectedGrade, setSelectedGrade] = useState(null);
  const [selectedExamType, setSelectedExamType] = useState(null);
  const [shuffledQuestions, setShuffledQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState({});
  const [showResultForCurrentQuestion, setShowResultForCurrentQuestion] =
    useState(false);
  const [quizFinished, setQuizFinished] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);

  const nextButtonRef = useRef(null);

  const loadQuestions = useCallback(
    async (grade, exam) => {
      setLoading(true);
      setError(null);
      try {
        const quiz = await fetchQuestions(grade, exam);
        setShuffledQuestions(quiz);
        setCurrentQuestionIndex(0);
        setUserAnswers({});
        setShowResultForCurrentQuestion(false);
        setQuizFinished(false);
      } catch (err) {
        if (err.code === "LEARNER_REQUIRED") {
          setSelectedExamType(null);
          openGate(() => {
            setSelectedExamType(exam);
            loadQuestions(grade, exam);
          });
          return;
        }
        setError("Không thể tải câu hỏi. Vui lòng thử lại.");
        console.error("Error loading questions:", err);
      } finally {
        setLoading(false);
      }
    },
    [fetchQuestions, openGate]
  );

  const handleChooseExam = (label) => {
    setSelectedExamType(label);
    loadQuestions(selectedGrade, label);
  };

  const handleSelect = (qid, option) => {
    if (!userAnswers.hasOwnProperty(qid)) {
      setUserAnswers((prev) => ({ ...prev, [qid]: option }));
      setShowResultForCurrentQuestion(true);
    }
  };

  const handleNextQuestion = () => {
    setShowResultForCurrentQuestion(false);
    if (currentQuestionIndex < shuffledQuestions.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
    } else {
      setQuizFinished(true);
    }
  };

  const handleRestartQuiz = () => {
    setShuffledQuestions([]); // Xóa câu hỏi cũ
    setCurrentQuestionIndex(0); // Đặt lại chỉ số câu hỏi
    setUserAnswers({}); // Xóa câu trả lời của người dùng
    setShowResultForCurrentQuestion(false); // Ẩn kết quả câu hỏi hiện tại
    setQuizFinished(false); // Đặt lại trạng thái hoàn thành bài kiểm tra
    setError(null); // Xóa lỗi (nếu có)
    loadQuestions(selectedGrade, selectedExamType); // Tải lại câu hỏi mới
  };

  const calculateScore = () => {
    let correctCount = 0;
    shuffledQuestions.forEach((q) => {
      if (userAnswers[q.id] === q.answer) {
        correctCount++;
      }
    });
    return correctCount * 0.25;
  };

  const currentQuestion = shuffledQuestions[currentQuestionIndex];

  // Ghi kết quả trắc nghiệm của học viên khi làm xong (điểm thang 10)
  useEffect(() => {
    if (!quizFinished || shuffledQuestions.length === 0) return;
    const correct = shuffledQuestions.filter((q) => userAnswers[q.id] === q.answer).length;
    recordActivity({
      type: "quiz_result",
      refKey: `quiz:${selectedGrade}:${selectedExamType}`,
      title: `Trắc nghiệm lớp ${selectedGrade} – ${selectedExamType}`,
      detail: `${correct}/${shuffledQuestions.length} câu đúng`,
      score: Math.round((correct / shuffledQuestions.length) * 100) / 10,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quizFinished]);

  useEffect(() => {
    if (window.MathJax && window.MathJax.typeset) {
      window.MathJax.typeset();
    }
  }, [
    shuffledQuestions,
    currentQuestionIndex,
    showResultForCurrentQuestion,
    quizFinished,
  ]);

  useEffect(() => {
    if (currentQuestion && currentQuestion.img) {
      setImageLoading(true);
      const img = new Image();
      img.src = `/quiz/${currentQuestion.img}`;
      img.onload = () => setImageLoading(false);
      img.onerror = () => {
        setImageLoading(false);
        console.error("Failed to load image:", img.src);
      };
    } else {
      setImageLoading(false);
    }
  }, [currentQuestion]);

  useEffect(() => {
    if (showResultForCurrentQuestion && nextButtonRef.current) {
      nextButtonRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [showResultForCurrentQuestion, currentQuestionIndex]);

  const score =
    currentQuestion &&
    userAnswers[currentQuestion.id] === currentQuestion.answer
      ? 1
      : 0;

  return (
    <div className="container mx-auto px-4 py-4 lg:py-12">
      <h2 className="text-xl lg:text-4xl font-extrabold text-gray-900 text-center mb-4 lg:mb-12 leading-tight">
        Trắc nghiệm môn Toán
      </h2>

      {!selectedGrade ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
          {[6, 7, 8, 9].map((grade) => (
            <div
              key={grade}
              onClick={() => setSelectedGrade(grade)}
              className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300 cursor-pointer flex flex-col items-center justify-center text-center group"
            >
              <GraduationCap className="w-16 h-16 text-indigo-500 group-hover:text-indigo-700 transition-colors duration-300 mb-4" />
              <h3 className="text-2xl font-bold text-gray-800 group-hover:text-indigo-700 transition-colors duration-300">
                Lớp {grade}
              </h3>
              <p className="text-gray-500 mt-2">Chọn để bắt đầu</p>
            </div>
          ))}
        </div>
      ) : !selectedExamType ? (
        <>
          <div className="flex justify-between items-center mb-6">
            <button
              onClick={() => setSelectedGrade(null)}
              className="flex items-center gap-2 text-indigo-600 hover:text-indigo-800 font-medium"
            >
              <ArrowLeft className="w-5 h-5" />
              Quay lại chọn lớp
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {/* {["Giữa HKI", "Cuối HKI", "Giữa HKII", "Cuối HKII"].map((label) => ( */}
            {["Giữa HKI"].map((label) => (
              <div
                key={label}
                onClick={() => handleChooseExam(label)}
                className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300 cursor-pointer flex flex-col items-center justify-center text-center group"
              >
                <FileQuestion className="w-16 h-16 text-purple-500 group-hover:text-purple-700 transition-colors duration-300 mb-4" />
                <h3 className="text-2xl font-bold text-gray-800 group-hover:text-purple-700 transition-colors duration-300">
                  {label}
                </h3>
              </div>
            ))}
          </div>
        </>
      ) : (
        <>
          <div className="flex justify-between items-center mb-2 lg:mb-6">
            <button
              onClick={() => {
                setSelectedExamType(null);
                setShuffledQuestions([]);
                setCurrentQuestionIndex(0);
                setUserAnswers({});
                setShowResultForCurrentQuestion(false);
                setQuizFinished(false);
                setError(null);
              }}
              className="flex items-center gap-2 text-indigo-600 hover:text-indigo-800 font-medium"
            >
              <ArrowLeft className="w-5 h-5" />
              Quay lại chọn đề
            </button>
            <div className="text-sm text-gray-500 font-semibold">
              Lớp {selectedGrade} - {selectedExamType}
            </div>
          </div>

          {loading ? (
            <div className="text-center py-20">
              <div className="animate-spin rounded-full h-20 w-20 border-t-4 border-b-4 border-purple-600 mx-auto mb-5"></div>
              <p className="text-2xl text-gray-700">Đang tải câu hỏi...</p>
            </div>
          ) : error ? (
            <div className="text-center py-20">
              <p className="text-2xl text-red-600 font-semibold">
                Lỗi khi tải câu hỏi
              </p>
              <p className="text-lg text-gray-600 mt-3">
                Có lỗi xảy ra: {error}
              </p>
              <p className="text-gray-600 mt-2">Vui lòng thử lại sau.</p>
            </div>
          ) : shuffledQuestions.length === 0 &&
            selectedExamType &&
            !quizFinished ? (
            <div className="col-span-full text-center text-gray-600 text-xl py-20">
              Rất tiếc, hiện tại không có câu hỏi nào cho lựa chọn này. Vui lòng
              quay lại sau!
            </div>
          ) : quizFinished ? (
            <div className="text-center py-12 bg-white rounded-lg shadow-xl">
              <Award className="w-24 h-24 text-yellow-500 mx-auto mb-4" />
              <h3 className="text-3xl font-bold text-gray-800 mb-4">
                Hoàn thành bài kiểm tra!
              </h3>
              <p className="text-xl text-gray-700 mb-3 flex items-center justify-center gap-2">
                <BarChart2 className="w-5 h-5" />
                Bạn đã trả lời đúng {calculateScore() / 0.25} /{" "}
                {shuffledQuestions.length} câu.
              </p>
              <p className="text-3xl font-bold text-indigo-700 mb-6 flex items-center justify-center gap-2">
                Điểm của bạn: {calculateScore()}
              </p>
              <button
                onClick={handleRestartQuiz}
                className="mt-4 px-8 py-3 bg-indigo-600 text-white rounded-lg text-lg font-semibold hover:bg-indigo-700 inline-flex items-center gap-2 transition-colors duration-300"
              >
                <ArrowRight className="w-5 h-5" />
                Làm lại bài khác
              </button>
            </div>
          ) : (
            currentQuestion && (
              <div className="bg-white p-3 lg:p-6 rounded-lg shadow-xl animate-fade-in">
                <div className="flex justify-between items-center mb-2 lg:mb-6 border-b lg:pb-4">
                  <p className="font-semibold lg:text-lg text-gray-600">
                    Câu hỏi {currentQuestionIndex + 1} /{" "}
                    {shuffledQuestions.length}
                  </p>
                  <p className="lg:text-lg text-gray-600">(0.25 điểm)</p>
                </div>

                <div key={currentQuestion.id} className="mb-8">
                  <p className="font-semibold lg:text-xl mb-2 lg:mb-4 text-gray-800 flex items-center gap-3">
                    <span
                      dangerouslySetInnerHTML={{
                        __html: currentQuestion.question,
                      }}
                    />
                  </p>
                  {currentQuestion.img && (
                    <div className="my-6 flex justify-center">
                      {imageLoading ? (
                        <div className="flex items-center justify-center w-full h-64 bg-gray-100 rounded-lg">
                          <Loader2 className="w-12 h-12 text-purple-400 animate-spin" />
                        </div>
                      ) : (
                        <img
                          src={`/quiz/${currentQuestion.img}`}
                          alt="Question Illustration"
                          className="max-w-[200px] lg:max-w-[400px] h-auto"
                        />
                      )}
                    </div>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2 lg:mt-6">
                    {currentQuestion.options.map((option, idx) => {
                      const isSelected =
                        userAnswers?.[currentQuestion.id] === option;
                      const isCorrect = option === currentQuestion.answer;
                      const isWrongSelected = isSelected && !isCorrect;

                      let baseStyle =
                        "border rounded-lg px-2 lg:px-5 py-2 lg:py-4 text-left transition-all duration-300 lg:text-xl";

                      if (showResultForCurrentQuestion) {
                        if (isCorrect) {
                          baseStyle +=
                            " bg-green-100 border-green-500 text-green-800";
                        } else if (isWrongSelected) {
                          baseStyle +=
                            " bg-red-100 border-red-500 text-red-800";
                        } else {
                          baseStyle +=
                            " bg-gray-100 text-gray-500 border-gray-300";
                        }
                      } else {
                        baseStyle += isSelected
                          ? " bg-indigo-600 text-white border-indigo-600 shadow-md"
                          : " bg-white hover:bg-gray-50 border-gray-300 text-gray-800";
                      }

                      return (
                        <button
                          key={idx}
                          onClick={() =>
                            handleSelect(currentQuestion.id, option)
                          }
                          className={`${baseStyle} flex justify-between items-center`}
                          disabled={showResultForCurrentQuestion}
                        >
                          <span className="flex items-center gap-3">
                            {isSelected && !showResultForCurrentQuestion && (
                              <CircleDot className="w-5 h-5" />
                            )}
                            <span
                              dangerouslySetInnerHTML={{ __html: option }}
                            />
                          </span>
                          {showResultForCurrentQuestion && isCorrect && (
                            <CheckCircle2 className="text-green-600 w-6 h-6" />
                          )}
                          {showResultForCurrentQuestion && isWrongSelected && (
                            <XCircle className="text-red-600 w-6 h-6" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="text-center mt-8">
                  {showResultForCurrentQuestion && (
                    <>
                      <div
                        className={`text-2xl font-bold ${
                          score ? "text-green-700" : "text-red-700"
                        } mb-4 flex items-center justify-center gap-2`}
                      >
                        {score ? "Bạn đã trả lời đúng!" : "Bạn đã trả lời sai!"}
                      </div>
                      <button
                        onClick={handleNextQuestion}
                        ref={nextButtonRef}
                        className="px-8 py-3 bg-indigo-600 text-white rounded-lg text-lg font-semibold hover:bg-indigo-700 inline-flex items-center gap-2 transition-colors duration-300"
                      >
                        {currentQuestionIndex < shuffledQuestions.length - 1 ? (
                          <>
                            <ArrowRight className="w-5 h-5" />
                            Câu tiếp theo
                          </>
                        ) : (
                          <>
                            <Award className="w-5 h-5" />
                            Xem kết quả
                          </>
                        )}
                      </button>
                    </>
                  )}
                </div>
              </div>
            )
          )}
        </>
      )}
    </div>
  );
};

export default QuizPage;
